// ============================================================
// FILE: AdminContext.tsx
// ============================================================

import React, { createContext, useState, useContext, useMemo } from "react";
import type { SmeFeatureFee, ThirdPartyFeature } from "@/app/types/adminMenu";

type AdminContextType = {
  disableOnRole: boolean;
  setDisableOnRole: React.Dispatch<React.SetStateAction<boolean>>;
  isSuperAdmin: boolean;
  setIsSuperAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  adminName: string;
  setAdminName: React.Dispatch<React.SetStateAction<string>>;
  permissions: string[];
  setPermissions: React.Dispatch<React.SetStateAction<string[]>>;

  // third-party-services: the currently selected third-party card
  // (e.g. ERP / POS / Ecommerce). Persists across route changes so the
  // correct card stays highlighted/active after navigating to a
  // sub-feature screen (Zoho/Stripe/Magnati/etc.) and back.
  selectedThirdPartyFeature: ThirdPartyFeature | null;
  setSelectedThirdPartyFeature: React.Dispatch<
    React.SetStateAction<ThirdPartyFeature | null>
  >;

  // third-party-services: full object of whichever sub-feature's
  // Request button was last clicked (includes compConfigFeatureFeeList).
  selectedThirdPartySubFeature: SmeFeatureFee | null;
  setSelectedThirdPartySubFeature: React.Dispatch<
    React.SetStateAction<SmeFeatureFee | null>
  >;
};

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [disableOnRole, setDisableOnRole] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(true);
  const [adminName, setAdminName] = useState<string>("Demo Admin");
  const [permissions, setPermissions] = useState<string[]>([
    "read",
    "write",
    "delete",
  ]);
  const [selectedThirdPartyFeature, setSelectedThirdPartyFeature] =
    useState<ThirdPartyFeature | null>(null);
  // third-party-services
  const [selectedThirdPartySubFeature, setSelectedThirdPartySubFeature] =
    useState<SmeFeatureFee | null>(null);

  // ✅ Memoize the context value so it doesn’t change every render
  const value = useMemo(
    () => ({
      disableOnRole,
      setDisableOnRole,
      isSuperAdmin,
      setIsSuperAdmin,
      adminName,
      setAdminName,
      permissions,
      setPermissions,

      selectedThirdPartySubFeature,
      setSelectedThirdPartySubFeature,

      selectedThirdPartyFeature,
      setSelectedThirdPartyFeature,
    }),
    [
      disableOnRole,
      isSuperAdmin,
      adminName,
      permissions,
      selectedThirdPartySubFeature,
      selectedThirdPartyFeature,
    ],
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdminContext must be used within an AdminProvider");
  }
  return context;
};
